/**
 * トップページ (線区未選択時) に表示する線区選択ランディング。
 *
 * 背景:
 *   従来のトップページは全線区の放送地点をテーブルに全件表示していたが、
 *   実運用では「まず線区を選ぶ」のが最初の操作であり、全件一覧を見る場面が無い。
 *   そこで線区選択を主役にした画面へ変更した。
 *
 * 仕様:
 *   - **1 段階選択** (1 クリックで一覧 + 地図へ遷移)
 *       線区カードの中に方面ボタンを並べ、押した時点で `onSelect(routeId, directionName)`
 *       が呼ばれる。以前は「線区を選ぶ → 方面を選ぶ」の 2 段階だったが、
 *       方面は 2 つ程度しか無く、1 画面に収まるため操作を 1 段階へ短縮した。
 *   - 全線区を表示する (`driver_app_supported` による出し分けは行わない)。
 *   - カードは `routes-master.json` の `route_color` / `route_text_color` を用いた
 *     線区カラーで縁取り + 左端のアクセントラインを引く
 *     (可読性のため文字色はマスタ指定値を尊重する)。
 */
import { Spinner } from './Spinner';
import { getRouteColor, type RouteMaster } from '../data/routes';

export interface RouteLandingSelectorProps {
  /** 線区マスタ (全線区を表示する) */
  routes: RouteMaster[];
  /**
   * 線区 + 方面が確定したときに呼ばれる。
   * 親側で選択状態を保存し、一覧 + 地図画面へ遷移させる。
   */
  onSelect: (routeId: string, directionName: string) => void;
  /** 線区マスタ読み込み中フラグ (true のときはプレースホルダを表示) */
  loading?: boolean;
}

export function RouteLandingSelector({
  routes,
  onSelect,
  loading = false,
}: RouteLandingSelectorProps): React.JSX.Element {
  if (loading && routes.length === 0) {
    return (
      <div className="route-landing" data-testid="route-landing">
        <p className="route-landing__loading">
          <Spinner />
          線区一覧を読み込み中...
        </p>
      </div>
    );
  }

  return (
    <div className="route-landing" data-testid="route-landing">
      <h2 className="route-landing__title">線区・方面を選択してください</h2>
      <p className="route-landing__lead">
        方面ボタンを押すと、その線区・方面の放送地点一覧と地図が表示されます。
      </p>
      {routes.length === 0 ? (
        <p className="route-landing__empty" data-testid="route-landing-empty">
          線区一覧が取得できませんでした。
        </p>
      ) : (
        <ul className="route-landing__grid">
          {routes.map((r) => {
            const color = getRouteColor(routes, r.route_id);
            return (
              <li
                key={r.route_id}
                className="route-landing__card"
                data-testid={`route-landing-route-${r.route_id}`}
                style={{ borderColor: color.background }}
              >
                <span
                  className="route-landing__card-accent"
                  style={{ background: color.background }}
                  aria-hidden="true"
                />
                <span className="route-landing__card-name">{r.route_name}</span>
                <span className="route-landing__card-sub">{r.route_id}</span>
                <div className="route-landing__directions">
                  {r.directions.length === 0 ? (
                    // 方面が未登録の線区は「方面なし」で開けるようにする
                    <button
                      type="button"
                      className="route-landing__direction"
                      data-testid={`route-landing-open-${r.route_id}`}
                      style={{ borderColor: color.background }}
                      onClick={() => onSelect(r.route_id, '')}
                    >
                      方面を指定せずに開く
                    </button>
                  ) : (
                    r.directions.map((d) => (
                      <button
                        key={d.direction_name}
                        type="button"
                        className="route-landing__direction"
                        data-testid={`route-landing-direction-${r.route_id}-${d.direction_name}`}
                        style={{ borderColor: color.background }}
                        onClick={() => onSelect(r.route_id, d.direction_name)}
                      >
                        {d.direction_name}
                      </button>
                    ))
                  )}
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
